// pages/admin/AdminSyncSection.tsx
import { Database, Eye, Play } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useTestMssql, usePreviewSync, useRunSync } from "@/hooks/useAdminSync";
import { SyncPreview } from "@/pages/admin/SyncPreview";

export function AdminSyncSection() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const testConn = useTestMssql();
  const preview = usePreviewSync();
  const run = useRunSync();

  const handleTest = async () => {
    try {
      const res = await testConn.mutateAsync();
      toast({ title: "Connexion MSSQL", description: res?.message ?? "OK ✅" });
    } catch (e: any) {
      toast({
        title: "Échec connexion MSSQL",
        description: e?.message ?? "Erreur inconnue",
        variant: "destructive",
      });
    }
  };

  const handlePreview = async () => {
    try {
      await preview.mutateAsync();
      toast({ title: "Prévisualisation prête ✅" });
    } catch (e: any) {
      toast({
        title: "Échec prévisualisation",
        description: e?.message ?? "Erreur inconnue",
        variant: "destructive",
      });
    }
  };

  const handleRun = async () => {
    try {
      const res = await run.mutateAsync();
      toast({
        title: "Import terminé ✅",
        description: res?.summary
          ? JSON.stringify(res.summary)
          : "Utilisateurs, rapports et permissions synchronisés.",
      });
      // rafraîchir listes après import
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["reports"] });
    } catch (e: any) {
      toast({
        title: "Échec import",
        description: e?.message ?? "Erreur inconnue",
        variant: "destructive",
      });
    }
  };

  return (
    <section className="space-y-6">
      <Card className="bg-card border-glass p-4 md:p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Database className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="font-semibold text-lg">Synchronisation MSSQL</div>
              <div className="text-muted-foreground text-sm">
                Importer les utilisateurs & procédures stockées depuis SQL Server (table jointe STAT_DEST × STATISTIQUE).
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleTest}
              disabled={testConn.isPending}
            >
              <Database className="h-4 w-4" />
              Tester la connexion
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={handlePreview}
              disabled={preview.isPending}
            >
              <Eye className="h-4 w-4" />
              Prévisualiser
            </Button>
            <Button
              className="gap-2"
              onClick={handleRun}
              disabled={run.isPending}
            >
              <Play className="h-4 w-4" />
              Importer maintenant
            </Button>
          </div>
        </div>

        {(preview.data || run.data) && (
          <div className="mt-4">
            <SyncPreview data={run.data?.summary ?? preview.data} />
          </div>
        )}
      </Card>
    </section>
  );
}
